import React from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Autocomplete,
  AutocompleteItem
} from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';
import { organizationsAutocompleteQueryAtom, organizationsListParamsAtom, addDomainToOrganization } from '@/store';
import { toast } from 'react-toastify';

interface AddDomainModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function AddDomainModal({ isOpen, onOpenChange, onSuccess }: AddDomainModalProps) {
  const [title, setTitle] = React.useState('');
  const [domain, setDomain] = React.useState('');
  const [tokenLimit, setTokenLimit] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [selectedOrgId, setSelectedOrgId] = React.useState<string | null>(null);
  const [selectedOrgName, setSelectedOrgName] = React.useState<string>('');
  const [searchText, setSearchText] = React.useState('');

  // Use organizationsAutocompleteQueryAtom with custom params for autocomplete
  const [params, setParams] = useAtom(organizationsListParamsAtom);
  const { data: organizationsData, isLoading: isLoadingOrgs } = useAtomValue(organizationsAutocompleteQueryAtom);
  const organizations = organizationsData?.data || [];

  // Update params when search text changes (with debounce)
  React.useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      setParams((prev) => ({
        ...prev,
        page: 1,
        filters: searchText ? { name: searchText } : {}
      }));
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchText, isOpen, setParams]);

  // Reset params when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setParams((prev) => ({
        ...prev,
        page: 1,
        filters: {}
      }));
      setSearchText('');
      setSelectedOrgId(null);
      setSelectedOrgName('');
    }
  }, [isOpen, setParams]);

  const handleSubmit = React.useCallback(async () => {
    if (!selectedOrgId || !title || !domain) return;

    try {
      setIsSubmitting(true);

      const parsed = tokenLimit.trim() ? parseInt(tokenLimit, 10) : null;
      const token_limit =
        parsed != null && !Number.isNaN(parsed) && parsed >= 1 ? parsed : null;

      const payload: { title: string; domain: string; token_limit?: number | null } = {
        title,
        domain,
        token_limit
      };

      await addDomainToOrganization(parseInt(selectedOrgId), payload);

      // Show success message
      toast.success(`Domain ${domain} added successfully`);

      // Reset form
      setTitle('');
      setDomain('');
      setTokenLimit('');
      setSelectedOrgId(null);
      setSelectedOrgName('');
      setSearchText('');

      // Close modal and trigger refresh
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Failed to add domain:', error);
      // Error is handled by axios interceptor
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedOrgId, title, domain, onOpenChange, onSuccess]);

  const handleKeyPress = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !isSubmitting && selectedOrgId && title && domain) {
        handleSubmit();
      }
    },
    [handleSubmit, isSubmitting, selectedOrgId, title, domain]
  );

  const handleClose = React.useCallback(() => {
    setTitle('');
    setDomain('');
    setTokenLimit('');
    setSelectedOrgId(null);
    setSelectedOrgName('');
    setSearchText('');
    onOpenChange(false);
  }, [onOpenChange]);

  const isFormValid = Boolean(selectedOrgId && title && domain);

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="md" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Add Domain</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4" onKeyDown={handleKeyPress}>
                {/* Workspace Selection - Autocomplete */}
                <Autocomplete
                  label="Workspace"
                  placeholder="Search and select workspace..."
                  inputValue={selectedOrgId ? selectedOrgName : searchText}
                  isLoading={isLoadingOrgs}
                  selectedKey={selectedOrgId}
                  isClearable
                  onInputChange={(value) => {
                    if (!selectedOrgId) {
                      setSearchText(value);
                    }
                  }}
                  onSelectionChange={(key) => {
                    if (key) {
                      const orgId = key as string;
                      const org = organizations.find((o) => o.id.toString() === orgId);
                      if (org) {
                        setSelectedOrgId(orgId);
                        setSelectedOrgName(org.name);
                        setSearchText('');
                      }
                    }
                  }}
                  onClear={() => {
                    setSelectedOrgId(null);
                    setSelectedOrgName('');
                    setSearchText('');
                  }}
                  onOpenChange={() => {}} // Prevent modal from closing
                  isRequired
                >
                  {organizations.map((org) => (
                    <AutocompleteItem key={org.id.toString()}>
                      <div className="flex flex-col">
                        <span className="font-medium">{org.name}</span>
                        <span className="text-default-500 text-xs">
                          {org.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </AutocompleteItem>
                  ))}
                </Autocomplete>

                {/* Title */}
                <Input
                  label="Title"
                  placeholder="Enter domain title"
                  value={title}
                  onValueChange={setTitle}
                  isRequired
                />

                {/* Domain */}
                <Input
                  label="Domain"
                  placeholder="Enter domain (e.g., example.com)"
                  value={domain}
                  onValueChange={setDomain}
                  isRequired
                />

                {/* Domain Event Cap (optional; empty = unlimited) */}
                <Input
                  type="number"
                  min={1}
                  label="Domain Event Cap"
                  placeholder="Leave empty for unlimited"
                  value={tokenLimit}
                  onValueChange={setTokenLimit}
                  description="Max events processed for this domain. Leave empty for unlimited."
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>
                Cancel
              </Button>
              <Button color="primary" isLoading={isSubmitting} onPress={handleSubmit} isDisabled={!isFormValid}>
                Add Domain
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
